/**
 * Turning the server's 0-100 performance score into the "3,4 / 5" the dial shows: the score is
 * the share of the scale the employee has reached, so it multiplies the number of levels. Nothing
 * here is a literal 5, so a company with three or seven levels reads a scale that is true for it.
 */

interface ScoreOutOfLevelsParams {
    score: number | null | undefined;
    totalLevels: number | null | undefined;
}

/**
 * The score expressed on the company's own level scale, or `null` when there is nothing to say:
 * no levels configured, or no score (never evaluated, or nothing observed). `null` is not zero --
 * zero is a real result that means "the lowest level", and stating it about someone who has never
 * been evaluated would be a claim, not a blank.
 */
export const scoreOutOfLevels = ({ score, totalLevels }: ScoreOutOfLevelsParams): number | null => {
    if (!totalLevels || totalLevels <= 0) {
        return null;
    }
    if (score === null || score === undefined || !Number.isFinite(score)) {
        return null;
    }

    return Math.min(totalLevels, Math.max(0, (score / 100) * totalLevels));
};

interface ShareOfScaleParams {
    value: number | null;
    totalLevels: number;
}

/**
 * How much of the arc the dial fills, in percent, read from the very figure printed under it: a
 * dial reading "5,0 / 5" draws a full arc. Empty when there is no figure.
 */
export const shareOfScale = ({ value, totalLevels }: ShareOfScaleParams): number =>
    value === null || totalLevels <= 0 ? 0 : (value / totalLevels) * 100;
